import React, { useEffect, useMemo, useState } from "react"
import { useField, useFormikContext } from "formik"
import moment from "moment"

interface DateSelectorProps {
  name?: string
  disabledDates?: (Date | string)[]
  futureOnly?: boolean
  onChange?: (date: moment.Moment) => void
  isFormik?: boolean
  value?: moment.Moment
  dateFormat?: string
  disabled?: boolean
  mode?: "select" | "toggle"
  activeDates?: (Date | string)[]
  onToggle?: (date: moment.Moment, next: boolean) => void
  month?: moment.Moment
  onMonthChange?: (m: moment.Moment) => void
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// Helper function to chunk array into weeks
const chunkIntoWeeks = (arr: any[]) => {
  const weeks: any[][] = []
  for (let i = 0; i < arr.length; i += 7) {
    weeks.push(arr.slice(i, i + 7))
  }
  return weeks
}

// Separate Formik wrapper component
const FormikDateSelector: React.FC<DateSelectorProps> = (props) => {
  const formikContext = useFormikContext()
  const [field, meta] = useField(props.name || "")

  return <DateSelectorBase {...props} formikContext={formikContext} field={field} meta={meta} />
}

// Base component that handles the calendar logic
const DateSelectorBase: React.FC<
  DateSelectorProps & {
    formikContext?: any
    field?: any
    meta?: any
  }
> = ({
  name = "",
  disabledDates = [],
  futureOnly = false,
  onChange,
  isFormik = false,
  value: externalValue,
  dateFormat = "YYYY-MM-DD",
  disabled = false,
  formikContext,
  field,
  meta,
  mode = "select",
  activeDates = [],
  onToggle,
  month,
  onMonthChange,
}) => {
  const initialMonth = useMemo(() => {
    const base =
      month ||
      (isFormik && field?.value ? moment(field.value) : null) ||
      (externalValue ? moment(externalValue) : null) ||
      moment()
    return moment(base).startOf("month")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [currentDate, setCurrentDate] = useState<moment.Moment>(initialMonth)
  const [internalSelectedDate, setInternalSelectedDate] = useState<moment.Moment | null>(null)

  useEffect(() => {
    if (!month) return
    setCurrentDate(moment(month).startOf("month"))
  }, [month])

  const selectedDate = useMemo(() => {
    if (isFormik && field?.value) {
      return moment(field.value)
    }
    return externalValue || internalSelectedDate
  }, [isFormik, field?.value, externalValue, internalSelectedDate])

  const disabledDatesSet = useMemo(() => {
    const dateSet = new Set<string>()
    disabledDates.forEach((date) => {
      try {
        dateSet.add(moment(date).format(dateFormat))
      } catch (e) {
        console.warn("Invalid date provided:", date)
      }
    })
    return dateSet
  }, [disabledDates, dateFormat])

  const activeDatesSet = useMemo(() => {
    const set = new Set<string>()
    ;(activeDates || []).forEach((d) => {
      try {
        set.add(moment(d).format(dateFormat))
      } catch (e) {
        console.warn("Invalid active date:", d)
      }
    })
    return set
  }, [activeDates, dateFormat])

  const isDateDisabled = useMemo(() => {
    return (date: moment.Moment) => {
      const key = date.format(dateFormat)

      const today = moment().startOf("day")
      const isPast = futureOnly && date.isBefore(today)

      const isBlocked = disabledDatesSet.has(key)

      const hasAllowlist = (activeDates || []).length > 0
      const isNotAllowed = mode === "select" && hasAllowlist && !activeDatesSet.has(key)

      return isPast || isBlocked || isNotAllowed
    }
  }, [dateFormat, disabledDatesSet, futureOnly, mode, activeDates, activeDatesSet])

  const getDayClasses = (dayInfo: { disabled: boolean; selected: boolean; active?: boolean }): string => {
    let classes = "day "

    if (dayInfo.disabled) {
      classes += "disabled"
    } else if (mode === "toggle" && dayInfo.active) {
      classes += "selected"
    } else if (dayInfo.selected && isFormik) {
      classes += "selected"
    } else if (dayInfo.selected) {
      classes += "selected"
    }

    return classes
  }

  const { startingDayOfWeek, totalDays, currentMonthStr } = useMemo(() => {
    const firstDay = moment(currentDate).startOf("month")
    const lastDay = moment(currentDate).endOf("month")

    return {
      startingDayOfWeek: firstDay.day(),
      totalDays: lastDay.date(),
      currentMonthStr: `${MONTHS[currentDate.month()]} ${currentDate.year()}`,
    }
  }, [currentDate])

  const calendarDays = useMemo(() => {
    const paddingDays = Array(startingDayOfWeek).fill(null)
    const monthDays = Array.from({ length: totalDays }, (_, index) => {
      const day = index + 1
      const date = moment(currentDate).date(day)
      const key = date.format(dateFormat)
      return {
        day,
        disabled: disabled || isDateDisabled(date),
        selected: !!selectedDate && date.isSame(selectedDate, "day"),
        active: mode === "toggle" ? activeDatesSet.has(key) : false,
        date,
      }
    })

    const allDays = [...paddingDays, ...monthDays]
    const remainingDays = 7 - (allDays.length % 7)
    if (remainingDays < 7) {
      allDays.push(...Array(remainingDays).fill(null))
    }

    return chunkIntoWeeks(allDays)
  }, [
    currentDate,
    totalDays,
    startingDayOfWeek,
    selectedDate,
    disabled,
    mode,
    activeDatesSet,
    dateFormat,
    isDateDisabled,
  ])

  const setMonth = (next: moment.Moment) => {
    const normalized = moment(next).startOf("month")
    setCurrentDate(normalized)
    onMonthChange?.(normalized)
  }

  const handleDateClick = (date: moment.Moment) => {
    if (disabled || isDateDisabled(date)) return

    if (mode === "toggle") {
      const key = date.format(dateFormat)
      const next = !activeDatesSet.has(key)
      onToggle?.(date, next)
      return
    }

    if (isFormik && formikContext) {
      const formattedDate = date.format(dateFormat)
      formikContext.setFieldValue(name, formattedDate, true)
      formikContext.setFieldTouched(name, true, false)
    } else {
      setInternalSelectedDate(date)
      onChange?.(date)
    }
  }

  const changeMonth = (increment: number) => {
    if (disabled) return
    setMonth(moment(currentDate).add(increment, "months"))
  }

  const changeYear = (increment: number) => {
    if (disabled) return
    setMonth(moment(currentDate).add(increment, "years"))
  }

  return (
    <div className={`DateSelector card ${disabled ? "disabled" : ""}`}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => changeYear(-1)}
            disabled={disabled}
          >
            &lt;&lt;
          </button>
          <button
            type="button"
            className="btn btn-secondary ms-1"
            onClick={() => changeMonth(-1)}
            disabled={disabled}
          >
            &lt;
          </button>

          <h5 className="mb-0 w-100 text-center">{currentMonthStr}</h5>

          <button
            type="button"
            className="btn btn-secondary me-1"
            onClick={() => changeMonth(1)}
            disabled={disabled}
          >
            &gt;
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => changeYear(1)}
            disabled={disabled}
          >
            &gt;&gt;
          </button>
        </div>

        <div className="container p-0">
          <div className="row">
            {DAYS.map((day) => (
              <div key={day} className="col p-1 text-center">
                <small className="text-muted">{day}</small>
              </div>
            ))}
          </div>

          {calendarDays.map((week, weekIndex) => (
            <div key={weekIndex} className="row">
              {week.map((dayInfo, dayIndex) => (
                <div key={dayIndex} className="col p-1 text-center">
                  {dayInfo ? (
                    <div
                      onClick={() => handleDateClick(dayInfo.date)}
                      className={getDayClasses({
                        disabled: dayInfo.disabled,
                        selected: dayInfo.selected,
                        active: dayInfo.active,
                      })}
                      style={{
                        width: "2.5rem",
                        height: "2.5rem",
                        cursor: dayInfo.disabled ? "not-allowed" : "pointer",
                      }}
                    >
                      {dayInfo.day}
                    </div>
                  ) : (
                    <div style={{ width: "2.5rem", height: "2.5rem" }} />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {isFormik && meta?.touched && meta?.error && (
          <div className="text-danger mt-2 small">{meta.error}</div>
        )}
      </div>
    </div>
  )
}

// Main component that decides which version to render
const DateSelector: React.FC<DateSelectorProps> = (props) => {
  if (props.isFormik) {
    return <FormikDateSelector {...props} />
  }
  return <DateSelectorBase {...props} />
}

export default DateSelector
